import ICreateProcedure from '@Shared.Angular/@types/core/contracts/commands/procedure/createProcedure';
import IPublishProcedure from '@Shared.Angular/@types/core/contracts/commands/procedure/publishProcedure';
import { ProcedureViewMode } from '@Shared.Angular/@types/core/contracts/constants/procedureViewMode';
import { PublicType } from '@Shared.Angular/@types/core/contracts/constants/publicType';
import ICategoryDetail from '@Shared.Angular/@types/core/contracts/queryModel/category/categoryDetail';
import IProcedureActionDetail from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureActionDetail';
import IProcedureDetail from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureDetail';
import IProcedureFlowModel from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureFlowModel';
import IProcedureOverview from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureOverview';
import IProcedurePublishRequest from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedurePublishRequest';
import IProcedurePublishApprovalResult from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedurePublishApprovalResult';
import { IProcedureSchemaVersion } from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureSchemaVersion';
import IProcedureSummary from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureSummary';
import IProcedureTagSummary from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureTagSummary';
import IFlowinglySearchResult from '@Shared.Angular/@types/core/contracts/queryModel/search/flowinglySearchResult';
import ITagGroupDetail from '@Shared.Angular/@types/core/contracts/queryModel/tagGroup/tagGroupDetail';
import { Guid } from '@Shared.Angular/@types/guid';
import {
  IProcedureCreatedMessage,
  IProcedureDeletedMessage,
  IProcedurePublishedMessage,
  IProcedureUpdatedMessage,
  PubSubEvent
} from '@Shared.Angular/@types/pubSub';
import { FlowinglyPermissions } from './flowingly.constants';
import { Services } from '@Shared.Angular/@types/services';
import angular, { IHttpService } from 'angular';

export type CreateProcedureModel = {
  name: string;
  categoryId?: Guid;
  description?: string;
  ownerUserId?: Guid;
  objective?: string;
  background?: string;
};

export class ProcedureApiService {
  private apiBaseUrl: string;
  public deferedProcedures: angular.IDeferred<IProcedureOverview[]>;
  public deferedProcedureTags: angular.IDeferred<IProcedureTagSummary[]>;
  public proceduresLoaded = false;
  constructor(
    private $q: angular.IQService,
    private $http: IHttpService,
    private APP_CONFIG: Services.APP_CONFIG,
    private pubsubService: Services.PubSubService,
    private permissionsService: Services.PermissionsService,
    private sessionService: Services.SessionService
  ) {
    this.apiBaseUrl = this.APP_CONFIG.apiBaseUrl + 'procedures/';
    this.deferedProcedures = this.$q.defer<IProcedureOverview[]>();
    this.deferedProcedureTags = this.$q.defer<IProcedureTagSummary[]>();
    if (!this.APP_CONFIG.enableTagging) {
      this.deferedProcedureTags.resolve([]);
    }

    const subscriberId = 'procedureApiService';
    this.pubsubService.subscribe(
      'PROCEDURE_CREATED',
      (event, message: IProcedureCreatedMessage) => {
        this.deferedProcedures.promise.then((procedures) => {
          if (!procedures.find((p) => p.id === message.overview.id)) {
            this.updateOverviewPermissions(
              message.overview,
              message.summary.createdById
            );
            procedures.push(message.overview);
          }
          this.deferedProcedures = this.$q.defer<IProcedureOverview[]>();
          this.deferedProcedures.resolve(procedures);
        });
      },
      subscriberId
    );
    this.pubsubService.subscribeMany(
      ['PROCEDURE_UPDATED', 'PROCEDURE_PUBLISHED'],
      (
        event,
        message: IProcedureUpdatedMessage | IProcedurePublishedMessage
      ) => {
        this.deferedProcedures.promise.then((procedures) => {
          const index = procedures.findIndex(
            (procedure) => procedure.id === message.overview.id
          );
          this.updateOverviewPermissions(
            message.overview,
            message.summary.createdById
          );

          if (index !== -1) {
            procedures[index] = message.overview;
          } else {
            procedures.push(message.overview);
          }

          this.deferedProcedures = this.$q.defer<IProcedureOverview[]>();
          this.deferedProcedures.resolve(procedures);
        });

        if (this.APP_CONFIG.enableTagging) {
          this.deferedProcedureTags.promise.then((procedureTags) => {
            const index = procedureTags.findIndex(
              (tagSummary) =>
                tagSummary.procedureId === message.tagSummary.procedureId
            );
            if (index !== -1) {
              procedureTags[index] = message.tagSummary;
            } else {
              procedureTags.push(message.tagSummary);
            }
            this.deferedProcedureTags = this.$q.defer<IProcedureTagSummary[]>();
            this.deferedProcedureTags.resolve(procedureTags);
          });
        }
      },
      subscriberId
    );
    this.pubsubService.subscribe(
      'PROCEDURE_DELETED',
      (event: PubSubEvent, message: IProcedureDeletedMessage) => {
        this.deferedProcedures.promise.then((procedures) => {
          const updatedProcedures = procedures.filter(
            (proc) => proc.id !== message.procedureId
          );
          this.deferedProcedures = this.$q.defer<IProcedureOverview[]>();
          this.deferedProcedures.resolve(updatedProcedures);
        });
      },
      subscriberId
    );
  }

  private updateOverviewPermissions(
    overview: IProcedureOverview,
    createdById: Guid
  ) {
    const currentUserId = this.sessionService.getUser()?.id;
    const isCreatedByCurrentUser = createdById === currentUserId;

    overview.userCanEdit =
      this.permissionsService.currentUserHasPermission(
        FlowinglyPermissions.PROCEDURE_UPDATE
      ) ||
      (isCreatedByCurrentUser &&
        this.permissionsService.currentUserHasPermission(
          FlowinglyPermissions.PROCEDURE_UPDATE_OWN
        ));

    overview.userCanPublish =
      this.permissionsService.currentUserHasPermission(
        FlowinglyPermissions.PROCEDURE_PUBLISH
      ) ||
      (isCreatedByCurrentUser &&
        this.permissionsService.currentUserHasPermission(
          FlowinglyPermissions.PROCEDURE_PUBLISH_OWN
        ));

    overview.userCanDelete =
      this.permissionsService.currentUserHasPermission(
        FlowinglyPermissions.PROCEDURE_DELETE
      ) ||
      (isCreatedByCurrentUser &&
        this.permissionsService.currentUserHasPermission(
          FlowinglyPermissions.PROCEDURE_DELETE_OWN
        ));
  }

  createProcedure(model: CreateProcedureModel) {
    return this.$http.post<Guid>(this.apiBaseUrl, model).then((response) => {
      return response.data;
    });
  }

  importProcedure(procedure: ICreateProcedure) {
    return this.$http
      .post<Guid>(this.apiBaseUrl, procedure)
      .then((response) => {
        return response.data;
      });
  }

  copyProcedure(existingProcedureId: Guid, newProcedureName: string) {
    return this.$http
      .post<Guid>(this.apiBaseUrl + existingProcedureId, {
        newProcedureName
      })
      .then((response) => {
        return response.data;
      });
  }

  publishProcedure(command: IPublishProcedure) {
    return this.$http
      .put<IProcedureDetail>(
        this.apiBaseUrl + command.id + '/published',
        command
      )
      .then((response) => {
        return response.data;
      });
  }

  createProcedurePublishRequest(command: IPublishProcedure) {
    return this.$http
      .post<IProcedureDetail>(
        this.apiBaseUrl + command.id + '/publishRequests',
        command
      )
      .then((response) => {
        return response.data;
      });
  }

  approveProcedurePublishRequest(command: IPublishProcedure) {
    return this.$http
      .post<IProcedurePublishApprovalResult>(
        this.apiBaseUrl + command.id + '/publishRequests/approve',
        command
      )
      .then((response) => {
        return response.data;
      });
  }

  getProcedurePublishRequest(procedureId: Guid) {
    return this.$http
      .get<IProcedurePublishRequest>(
        this.apiBaseUrl + procedureId + '/publishRequests'
      )
      .then((response) => {
        return response.data;
      });
  }

  getProcedures() {
    const existingDeferred = this.deferedProcedures;
    this.deferedProcedures = this.$q.defer<IProcedureOverview[]>();
    return this.$http
      .get<IProcedureOverview[]>(this.apiBaseUrl)
      .then((response) => {
        this.proceduresLoaded = true;
        existingDeferred.resolve(response.data);
        this.deferedProcedures.resolve(response.data);
        return response.data;
      });
  }

  getProceduresPublishedToCurrentUser() {
    return this.$http
      .get<IProcedureSummary[]>(this.apiBaseUrl + 'published')
      .then((response) => {
        return response.data;
      });
  }

  getProcedure(
    id: Guid,
    viewMode: ProcedureViewMode,
    recordView = false,
    schemaId?: Guid
  ) {
    return this.$http
      .get<IProcedureDetail>(
        this.apiBaseUrl +
          id +
          `?viewMode=${viewMode}&recordView=${recordView}` +
          (schemaId ? `&schemaId=${schemaId}` : '')
      )
      .then((response) => {
        return response.data;
      });
  }

  getProcedureSchemaVersions(procedureId: Guid) {
    return this.$http
      .get<IProcedureSchemaVersion[]>(
        this.apiBaseUrl + procedureId + '/schemaVersions'
      )
      .then((response) => {
        return response.data.sort((a, b) => b.versionNumber - a.versionNumber);
      });
  }

  getPublicProcedure(id: Guid, businessId: Guid | null) {
    return this.$http
      .get<IProcedureDetail>(
        this.APP_CONFIG.apiBaseUrl +
          'procedure/' +
          id +
          (businessId ? `?businessId=${businessId}` : '')
      )
      .then((response) => {
        return response.data;
      });
  }

  enhanceProcedure(id: Guid) {
    return this.$http.put<void>(
      `${this.apiBaseUrl}${id}/actions/enhancements`,
      {}
    );
  }

  updateProcedure(procedure: IProcedureDetail) {
    return this.$http.put<void>(this.apiBaseUrl + procedure.id, procedure);
  }

  updateProcedureAction(action: IProcedureActionDetail) {
    return this.$http.put<void>(
      `${this.apiBaseUrl}${action.procedureId}/actions/${action.id}`,
      action
    );
  }

  addProcedureAction(action: IProcedureActionDetail) {
    return this.$http
      .post<Guid>(`${this.apiBaseUrl}${action.procedureId}/actions`, action)
      .then((response) => {
        return response.data;
      });
  }

  reorderProcedureActions(
    procedureId: Guid,
    actionOrders: { id: Guid; order: number }[]
  ) {
    return this.$http.put<void>(
      `${this.apiBaseUrl}${procedureId}/actions/reorder`,
      { actions: actionOrders }
    );
  }

  updateProcedurePublicType(id: Guid, publicType: PublicType | null) {
    return this.$http.put<void>(
      `${this.apiBaseUrl}${id}/publicType/${
        publicType === null ? '' : publicType
      }`,
      null
    );
  }

  deleteProcedure(procedureId: Guid) {
    return this.$http.delete<void>(this.apiBaseUrl + procedureId);
  }

  deleteProcedureAction(procedureId: Guid, actionId: Guid) {
    return this.$http.delete<void>(
      `${this.apiBaseUrl}${procedureId}/actions/${actionId}`
    );
  }

  restoreProcedureVersion(procedureId: Guid, schemaId: Guid) {
    return this.$http.post<void>(
      `${this.apiBaseUrl}${procedureId}/restore?schemaId=${schemaId}`,
      null
    );
  }

  getProcedurePublishedTo(procedureId: Guid) {
    return this.$http
      .get<IFlowinglySearchResult[]>(
        this.apiBaseUrl + procedureId + '/publishedEntities'
      )
      .then((response) => {
        return response.data;
      });
  }

  getProcedureTags(procedureId: Guid) {
    return this.$http
      .get<ITagGroupDetail[]>(this.apiBaseUrl + procedureId + '/tags')
      .then((response) => {
        return response.data;
      });
  }

  getUsedTagsInProcedures() {
    const existingDeferred = this.deferedProcedureTags;
    this.deferedProcedureTags = this.$q.defer<IProcedureTagSummary[]>();
    return this.$http
      .get<IProcedureTagSummary[]>(this.apiBaseUrl + 'tags')
      .then((response) => {
        existingDeferred.resolve(response.data);
        this.deferedProcedureTags.resolve(response.data);
        return response.data;
      });
  }

  getFlowModelsUsingProcedure(procedureId: Guid) {
    return this.$http
      .get<IProcedureFlowModel[]>(this.apiBaseUrl + procedureId + '/flowModels')
      .then((response) => {
        return response.data;
      });
  }

  getCategoriesUserCanPublishTo(
    procedureId: Guid,
    isPublishRequestSchema = false
  ) {
    return this.$http
      .get<ICategoryDetail[]>(
        this.apiBaseUrl +
          procedureId +
          '/categories?isPublishRequestSchema=' +
          isPublishRequestSchema
      )
      .then((response) => {
        return response.data;
      });
  }

  checkIsNameUnique(procedureName: string, procedureId?: Guid) {
    return this.$http
      .get<boolean>(this.apiBaseUrl + 'queryUniqueName', {
        params: {
          name: procedureName,
          ...(procedureId ? { procedureId: procedureId } : {})
        }
      })
      .then((response) => {
        return response.data;
      });
  }

  createProcedureFromVideo(videoFile: File, details: CreateProcedureModel) {
    const formData = new FormData();
    formData.append('file', videoFile);
    formData.append('name', details.name);
    if (details.categoryId) formData.append('categoryId', details.categoryId);
    if (details.description)
      formData.append('description', details.description);
    if (details.ownerUserId)
      formData.append('ownerUserId', details.ownerUserId);
    if (details.objective) formData.append('objective', details.objective);
    if (details.background) formData.append('background', details.background);

    return this.$http
      .post<Guid>(`${this.apiBaseUrl}generations`, formData, {
        transformRequest: angular.identity,
        headers: {
          'Content-Type': undefined as unknown as string
        }
      })
      .then((response) => {
        return response.data;
      });
  }
}

angular
  .module('flowingly.services')
  .factory('procedureApiService', [
    '$q',
    '$http',
    'APP_CONFIG',
    'pubsubService',
    'permissionsService',
    'sessionService',
    (
      $q: angular.IQService,
      $http: IHttpService,
      appConfig: Services.APP_CONFIG,
      pubsubService: Services.PubSubService,
      permissionsService: Services.PermissionsService,
      sessionService: Services.SessionService
    ) =>
      new ProcedureApiService(
        $q,
        $http,
        appConfig,
        pubsubService,
        permissionsService,
        sessionService
      )
  ]);

export type ProcedureApiServiceType = InstanceType<typeof ProcedureApiService>;
